import type { Locale } from "./i18n";
import { getDocsPathPrefix } from "./i18n";
import { DOC_LABELS, type DocPath } from "./doc-labels";

type CategoryKey =
  | "documentation"
  | "introduction"
  | "keyConcepts"
  | "naspipToken"
  | "paymentFlows"
  | "merchantIntegration"
  | "developers"
  | "tutorials"
  | "platform"
  | "apiReference"
  | "liveDapp"
  | "fluxisHome";

const CATEGORY_LABELS: Record<Locale, Record<CategoryKey, string>> = {
  en: {
    documentation: "Documentation",
    introduction: "Introduction",
    keyConcepts: "Key Concepts",
    naspipToken: "NASPIP Token",
    paymentFlows: "Payment Flows",
    merchantIntegration: "Merchant integration",
    developers: "Developers",
    tutorials: "Tutorials",
    platform: "Platform",
    apiReference: "API Reference",
    liveDapp: "Our Dapp",
    fluxisHome: "Fluxis Home",
  },
  es: {
    documentation: "Documentación",
    introduction: "Introducción",
    keyConcepts: "Conceptos clave",
    naspipToken: "Token NASPIP",
    paymentFlows: "Flujos de pago",
    merchantIntegration: "Integración para comercios",
    developers: "Desarrolladores",
    tutorials: "Tutoriales",
    platform: "Plataforma",
    apiReference: "Referencia de API",
    liveDapp: "Nuestra Dapp",
    fluxisHome: "Fluxis Home",
  },
  pt: {
    documentation: "Documentação",
    introduction: "Introdução",
    keyConcepts: "Conceitos-chave",
    naspipToken: "Token NASPIP",
    paymentFlows: "Fluxos de pagamento",
    merchantIntegration: "Integração para comerciantes",
    developers: "Desenvolvedores",
    tutorials: "Tutoriais",
    platform: "Plataforma",
    apiReference: "Referência da API",
    liveDapp: "Nossa Dapp",
    fluxisHome: "Fluxis Home",
  },
};

function doc(locale: Locale, path: DocPath, icon?: string) {
  const prefix = getDocsPathPrefix(locale);
  const item: { type: "doc"; file: string; label: string; icon?: string } = {
    type: "doc",
    file: `${prefix}${path}`,
    label: DOC_LABELS[locale][path],
  };
  if (icon) {
    item.icon = icon;
  }
  return item;
}

export function getCategoryLabels(locale: Locale) {
  return CATEGORY_LABELS[locale];
}

export function buildDocsNav(locale: Locale) {
  const labels = CATEGORY_LABELS[locale];

  return [
    {
      type: "category" as const,
      label: labels.introduction,
      icon: "info",
      items: [
        doc(locale, "introduction/overview", "binoculars"),
        doc(locale, "introduction/about", "badge-check"),
        doc(locale, "introduction/getting-started", "rocket"),
        doc(locale, "introduction/comparison", "scale"),
      ],
    },
    {
      type: "category" as const,
      label: labels.keyConcepts,
      icon: "key-round",
      items: [
        doc(locale, "key-concepts/uai", "hash"),
        doc(locale, "key-concepts/point-of-sale", "store"),
        doc(locale, "key-concepts/payment-request", "hand-coins"),
        doc(locale, "key-concepts/settlements-and-consolidation", "landmark"),
        {
          type: "category" as const,
          label: labels.naspipToken,
          icon: "signature",
          items: [
            doc(locale, "key-concepts/naspip/introduction"),
            doc(locale, "key-concepts/naspip/protocol-specification"),
            doc(locale, "key-concepts/naspip/security-model"),
            doc(locale, "key-concepts/naspip/implementation-guide"),
          ],
        },
        {
          type: "category" as const,
          label: labels.paymentFlows,
          icon: "workflow",
          items: [
            doc(locale, "payment-flows/std-flow", "smile"),
            doc(locale, "payment-flows/edge-cases", "crosshair"),
          ],
        },
      ],
    },
    {
      type: "category" as const,
      label: labels.merchantIntegration,
      icon: "store",
      items: [
        doc(locale, "merchant-integration/sign-up", "id-card"),
        doc(locale, "merchant-integration/auth-and-conf", "log-in"),
        doc(locale, "merchant-integration/create-payreq", "rocket"),
      ],
    },
    {
      type: "category" as const,
      label: labels.developers,
      icon: "folder-code",
      items: [
        {
          type: "category" as const,
          label: labels.tutorials,
          icon: "book-open-check",
          items: [
            doc(locale, "developers/tutorials/api-integration-guide", "hand-coins"),
            doc(locale, "developers/tutorials/payment-request", "receipt"),
            doc(locale, "developers/tutorials/subscriptions", "calendar-sync"),
          ],
        },
        doc(locale, "developers/api", "code"),
        doc(locale, "developers/mcp", "bot"),
        doc(locale, "developers/sdk", "chevrons-left-right-ellipsis"),
        doc(locale, "developers/compatible-apps", "smartphone"),
        doc(locale, "developers/webhooks", "webhook"),
        doc(locale, "developers/webhook-signature-verification", "shield-check"),
        {
          type: "category" as const,
          label: labels.platform,
          icon: "layers",
          items: [
            doc(locale, "developers/subscriptions", "repeat"),
            doc(locale, "developers/transactions", "list"),
            doc(locale, "developers/payment-links", "link"),
            doc(locale, "developers/webhook-events", "bell"),
            doc(locale, "developers/payment-notifications", "mail"),
            doc(locale, "developers/api-credentials", "key"),
            doc(locale, "developers/two-factor-auth", "shield-check"),
            doc(locale, "developers/team-and-roles", "users"),
            doc(locale, "developers/support-requests", "life-buoy"),
          ],
        },
        {
          type: "link" as const,
          to: "/api",
          label: labels.apiReference,
          icon: "book-open",
        },
      ],
    },
    doc(locale, "applications/use-cases", "lightbulb"),
    doc(locale, "faq", "badge-question-mark"),
    doc(locale, "roadmap", "map-pin"),
  ];
}

export function buildSharedTopLinks(
  locale: Locale,
  display: (_params: { context: unknown; auth: unknown }) => boolean,
) {
  const labels = CATEGORY_LABELS[locale];

  return [
    {
      type: "link" as const,
      to: "/api",
      label: labels.apiReference,
      description: "Complete API documentation",
      display,
    },
    {
      type: "link" as const,
      to: "https://dapp.fluxis.us",
      label: labels.liveDapp,
      description: labels.liveDapp,
      display,
    },
    {
      type: "link" as const,
      to: "https://fluxis.us",
      label: labels.fluxisHome,
      description: "Back to main site",
      display,
    },
  ];
}
